/**
 * PhotoAlbumCard — Admin card that mints, copies, and shares a public
 * "Live Progress Photos" URL. The URL grows as techs upload more photos.
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ImageSquare, Link as LinkIcon, Copy, ArrowClockwise, Trash, Eye,
  PaperPlaneTilt, EnvelopeSimple,
} from "@phosphor-icons/react";

export default function PhotoAlbumCard({ jobId, job }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try { setStats((await api.get(`/jobs/${jobId}/photo-album/stats`)).data); }
    catch { setStats({ has_link: false }); }
  };
  useEffect(() => { load(); }, [jobId]); // eslint-disable-line

  const url = stats?.path ? `${window.location.origin}${stats.path}` : "";

  const mint = async (rotate = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/photo-album${rotate ? "?rotate=true" : ""}`);
      setStats(r.data);
      const next = `${window.location.origin}${r.data.path}`;
      try { await navigator.clipboard.writeText(next); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
      toast.success(rotate ? "Rotated — old link is dead" : "Album link created");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Album link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const revoke = async () => {
    if (!window.confirm("Kill this album link?")) return;
    setBusy(true);
    try { await api.delete(`/jobs/${jobId}/photo-album`); toast.success("Revoked"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const label = job?.property_address ? ` — ${job.property_address}${job.unit ? ` #${job.unit}` : ""}` : "";
  const sms = `Live progress photos${label}${job?.work_order_number ? ` · WO ${job.work_order_number}` : ""}\n${url}\n\n(Photos auto-update as work continues)`;
  const emailSubj = `Live progress photos${label}`;
  const emailBody = `Live progress photo album:\n${url}\n\nPhotos auto-update as we work.\n\nField Radar`;

  return (
    <div className="mt-4 rounded-md border-2 border-cyan-200 bg-cyan-50/50 p-3" data-testid="album-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <ImageSquare weight="fill" size={18} className="text-cyan-700"/>
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-widest text-cyan-800">Live Progress Photos</div>
            <div className="text-[10px] text-slate-500">
              {stats?.has_link
                ? `${stats.view_count || 0} view${stats.view_count === 1 ? "" : "s"}${stats.last_viewed_at ? ` · last opened ${new Date(stats.last_viewed_at).toLocaleString()}` : " · not opened yet"}`
                : "Send owners one link that auto-updates as your techs upload photos"}
            </div>
          </div>
        </div>
        {stats?.has_link ? (
          <div className="flex items-center gap-1 flex-wrap">
            <button onClick={copy} className="h-8 px-3 bg-cyan-600 hover:bg-cyan-700 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1" data-testid="album-copy">
              <Copy weight="bold" size={11}/> {copied ? "Copied" : "Copy"}
            </button>
            <a href={`sms:?&body=${encodeURIComponent(sms)}`}
              className="h-8 px-3 bg-white border-2 border-cyan-300 text-cyan-700 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1" data-testid="album-sms">
              <PaperPlaneTilt weight="fill" size={11}/> SMS
            </a>
            <a href={`mailto:?subject=${encodeURIComponent(emailSubj)}&body=${encodeURIComponent(emailBody)}`}
              className="h-8 px-3 bg-white border-2 border-cyan-300 text-cyan-700 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1" data-testid="album-email">
              <EnvelopeSimple weight="bold" size={11}/> Email
            </a>
            <button onClick={() => window.open(url, "_blank")}
              className="h-8 px-3 bg-white border-2 border-cyan-300 text-cyan-700 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1" data-testid="album-open">
              <Eye weight="bold" size={11}/> Preview
            </button>
            <button onClick={() => mint(true)} disabled={busy} title="Rotate token" data-testid="album-rotate"
              className="h-8 px-2 bg-white border-2 border-slate-300 text-slate-600 text-[10px] font-black">
              <ArrowClockwise weight="bold" size={11}/>
            </button>
            <button onClick={revoke} disabled={busy} title="Kill this link" data-testid="album-revoke"
              className="h-8 px-2 bg-white border-2 border-red-200 text-red-600 text-[10px] font-black">
              <Trash weight="bold" size={11}/>
            </button>
          </div>
        ) : (
          <button onClick={() => mint(false)} disabled={busy}
            data-testid="album-create"
            className="h-9 px-4 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-black uppercase tracking-widest inline-flex items-center gap-1">
            <LinkIcon weight="bold" size={12}/> Create Album Link
          </button>
        )}
      </div>
      {stats?.has_link && url && (
        <div className="mt-2 text-[11px] font-mono text-slate-600 bg-white/70 border border-cyan-200 rounded px-2 py-1 truncate">
          {url}
        </div>
      )}
    </div>
  );
}
